// src/components/ui/calendar/week/WeekViewEventCard/WeekViewEventCard.jsx
import React from 'react';
import { cn } from '../../../../../utils/cn';
import { Badge } from '../../../badge';
import { Avatar, AvatarFallback } from '../../../avatar';
import {
  Calendar,
  Wrench,
  Network,
  MapPin
} from 'lucide-react';

const WeekViewEventCard = ({
  event,
  onClick,
  showEventPanel = false,
  isSelected = false,
  className = ''
}) => {
  const handleClick = (e) => {
    e.stopPropagation();
    if (!event.isPreview && onClick) {
      onClick(event);
    }
  };

  // Calculate duration for display
  const durationMinutes = event.duration_minutes ||
    Math.round((event.end.getTime() - event.start.getTime()) / (1000 * 60));
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  const durationText = hours > 0
    ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`)
    : `${minutes}m`;

  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Determine event type icon
  const getEventTypeIcon = () => {
    if (event.is_partner_service_event || event.is_referral_event) {
      return <Network className="w-3 h-3" />;
    }
    if (event.event_category === 'service') {
      return <Wrench className="w-3 h-3" />;
    }
    return <Calendar className="w-3 h-3" />;
  };

  const isServiceEvent = event.event_category === 'service';

  return (
    <div
      className={cn(
        // Glassmorphism card styling - matches DayView
        "rounded-xl cursor-pointer transition-all duration-300 h-full",
        "bg-slate-800/40 backdrop-blur-xl border border-slate-700/50",
        "hover:bg-slate-700/40 hover:border-slate-600/50 hover:scale-[1.02]",
        "shadow-lg hover:shadow-xl overflow-hidden group flex flex-col",
        event.isPreview && "border-dashed animate-preview-pulse",
        showEventPanel && !event.isPreview && "pointer-events-none opacity-60",
        isSelected && "ring-2 ring-white/50 ring-inset",
        className
      )}
      onClick={handleClick}
    >
      {/* Glassmorphism gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

      {/* Color accent bar (left edge) */}
      <div
        className={cn(
          "absolute top-0 left-0 w-1 h-full",
          event.isPreview && "w-0.5"
        )}
        style={{ backgroundColor: event.backgroundColor }}
      />

      <div className="relative z-10 flex flex-col h-full p-3 gap-1.5">
        {/* Header: Title + Icon */}
        <div className="flex items-center gap-1.5 min-w-0">
          <div style={{ color: event.backgroundColor }}>
            {getEventTypeIcon()}
          </div>
          <div className="font-bold text-sm leading-tight truncate text-slate-200 group-hover:text-white transition-colors">
            {event.title}
          </div>
        </div>

        {/* Time + Duration */}
        <div className="flex items-center justify-between text-xs text-slate-300">
          <span className="truncate">
            {event.start.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })} - {event.end.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </span>
          <Badge
            variant="outline"
            className="ml-1 text-[10px] px-1.5 py-0 h-4 flex-shrink-0 border-slate-600 text-slate-400"
          >
            {durationText}
          </Badge>
        </div>

        {/* Service Event Details */}
        {isServiceEvent && (
          <div className="flex flex-col gap-1.5 text-xs flex-1 min-h-0">
            {/* Customer with Avatar */}
            {event.customer?.name && (
              <div className="flex items-center gap-2 min-w-0">
                <Avatar className="h-5 w-5 flex-shrink-0 border border-slate-600">
                  <AvatarFallback
                    className="text-[9px] font-bold"
                    style={{
                      backgroundColor: `${event.backgroundColor}40`,
                      color: event.backgroundColor
                    }}
                  >
                    {getInitials(event.customer.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate font-medium text-slate-200">
                  {event.customer.name}
                </span>
              </div>
            )}

            {/* Service Type */}
            {event.service_type && (
              <div className="flex items-center gap-2 min-w-0 text-slate-300">
                <Wrench className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                <span className="truncate">{event.service_type}</span>
              </div>
            )}

            {/* Service Address */}
            {event.service_address && (
              <div className="flex items-center gap-2 min-w-0 text-slate-400">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate text-[11px]" title={event.service_address}>
                  {event.service_address}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Footer: Partner Badge / Preview Indicator */}
        <div className="mt-auto pt-1.5 flex flex-wrap gap-1 items-center border-t border-slate-700/50">
          {/* Partner Event Badge */}
          {(event.is_partner_service_event || event.is_referral_event) && (
            <Badge
              variant="outline"
              className="text-[9px] px-1.5 py-0.5 h-4 gap-1 bg-purple-500/20 text-purple-400 border-purple-500/30"
            >
              <Network className="w-2.5 h-2.5" />
              {event.is_referral_event ? 'Referral' : 'Partner'}
            </Badge>
          )}

          {/* Preview Indicator */}
          {event.isPreview && (
            <Badge
              variant="outline"
              className="text-[9px] px-1.5 py-0.5 h-4 bg-blue-500/20 text-blue-400 border-blue-500/30"
            >
              Preview
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeekViewEventCard;